/**
 * AdminTradeSignalsConfig — UI for iv-config DDB tunables. 22 numeric keys
 * grouped into 5 sections. Per-section Save button so a partial edit doesn't
 * push unrelated values. Loads the schema (label/type/range) from the backend
 * so the form always matches the Lambda's allowlist + validation rules.
 */
import React, { useEffect, useState } from 'react';
import { adminService } from '../../services/adminService';

type FieldSpec = { type: 'int' | 'float' | 'bool01'; min?: number; max?: number; section: string; label: string };
type RowState = { value: number | null; updatedAt: string | null; updatedBy: string | null };
type IvConfigResponse = {
  keys: Record<string, RowState>;
  schema: Record<string, FieldSpec>;
};

const SECTION_LABELS: Record<string, string> = {
  universe:  'Universe filter',
  dte:       'DTE window',
  moneyness: 'Option moneyness',
  delta_ivr: 'Option delta / IVR',
  liquidity: 'Option liquidity',
};

// Section order in the UI (controls render order; not a backend concern).
const SECTION_ORDER = ['universe', 'dte', 'moneyness', 'delta_ivr', 'liquidity'];

export const AdminTradeSignalsConfig: React.FC = () => {
  const [data, setData] = useState<IvConfigResponse | null>(null);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingSection, setSavingSection] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const resp = await adminService.getIvConfig();
      setData(resp);
      setEdits({});
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to load iv-config');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div className="admin-loading">Loading trade-signals config…</div>;
  if (error)   return <div className="admin-error">{error}</div>;
  if (!data)   return null;

  const sections: Record<string, string[]> = {};
  for (const [key, spec] of Object.entries(data.schema)) {
    (sections[spec.section] ||= []).push(key);
  }

  const handleSaveSection = async (section: string) => {
    const keysInSection = sections[section] || [];
    const updates: Record<string, number> = {};
    for (const k of keysInSection) {
      if (edits[k] === undefined) continue;
      const raw = edits[k].trim();
      if (raw === '') continue;
      const spec = data.schema[k];
      let n: number;
      if (spec.type === 'bool01') {
        n = (raw === '1' || raw === 'true' || raw === 'on') ? 1 : 0;
      } else {
        n = Number(raw);
        if (!Number.isFinite(n)) {
          setError(`${spec.label}: not a number (${raw})`);
          return;
        }
        if (spec.min !== undefined && n < spec.min) { setError(`${spec.label}: below min ${spec.min}`); return; }
        if (spec.max !== undefined && n > spec.max) { setError(`${spec.label}: above max ${spec.max}`); return; }
        if (spec.type === 'int') n = Math.round(n);
      }
      updates[k] = n;
    }
    if (Object.keys(updates).length === 0) {
      setError('Nothing changed in this section.');
      return;
    }
    try {
      setSavingSection(section);
      setError(null);
      await adminService.updateIvConfig(updates);
      setSavedAt(new Date().toISOString());
      await load();
    } catch (e: any) {
      const v = e?.response?.data?.validation;
      const detail = v ? Object.entries(v).map(([k, m]) => `${k}: ${m}`).join('; ') : '';
      setError((e?.response?.data?.error || e?.message || 'Save failed') + (detail ? ` — ${detail}` : ''));
    } finally {
      setSavingSection(null);
    }
  };

  const fmtUpdated = (iso: string | null, who: string | null) => {
    if (!iso) return null;
    const d = new Date(iso);
    return `Updated ${d.toLocaleString()}${who ? ` by ${who}` : ''}`;
  };

  return (
    <div className="admin-section">
      <h3>Trade Signals Config (iv-config table)</h3>
      <p style={{ color: '#94a3b8', fontSize: 13 }}>
        Tunable knobs read by the trade-signals Fargate data collector + v2 analyzer.
        Per-section Save — only fields you edited in that section are written. Changes apply on the next scheduled
        collector run (every 30 min during market hours).
      </p>
      {savedAt && (
        <div style={{ color: '#22c55e', marginBottom: 12 }}>
          ✓ Saved at {new Date(savedAt).toLocaleTimeString()}
        </div>
      )}

      {SECTION_ORDER.map((section) => {
        const keysInSection = sections[section];
        if (!keysInSection || keysInSection.length === 0) return null;
        return (
          <div key={section} style={{ marginBottom: 24, padding: 16, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8 }}>
            <h4 style={{ marginTop: 0 }}>{SECTION_LABELS[section] || section}</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(280px,1fr) 140px 1fr', gap: 8, alignItems: 'center' }}>
              {keysInSection.map((key) => {
                const spec = data.schema[key];
                const cur = data.keys[key];
                const editVal = edits[key];
                const inputValue = editVal !== undefined ? editVal : (cur?.value ?? '').toString();
                const rangeHint = spec.type === 'bool01'
                  ? '0 or 1'
                  : `${spec.min ?? '–'} … ${spec.max ?? '–'}${spec.type === 'int' ? ' (int)' : ''}`;
                return (
                  <React.Fragment key={key}>
                    <div>
                      <div style={{ fontWeight: 600 }}>{spec.label}</div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>{key} • {rangeHint}</div>
                    </div>
                    <input
                      type="text"
                      value={inputValue}
                      onChange={(e) => setEdits({ ...edits, [key]: e.target.value })}
                      style={{
                        padding: '6px 10px',
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.15)',
                        borderRadius: 4,
                        color: '#e5e7eb',
                        fontFamily: 'monospace',
                      }}
                    />
                    <div style={{ fontSize: 11, color: '#64748b' }}>
                      {cur?.value !== null && cur?.value !== undefined ? `current: ${cur.value}` : 'unset'}
                      {fmtUpdated(cur?.updatedAt, cur?.updatedBy) ? <> • {fmtUpdated(cur.updatedAt, cur.updatedBy)}</> : null}
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
            <button
              onClick={() => handleSaveSection(section)}
              disabled={savingSection !== null}
              style={{
                marginTop: 12,
                padding: '8px 16px',
                background: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: 4,
                cursor: savingSection ? 'wait' : 'pointer',
                opacity: savingSection ? 0.6 : 1,
              }}
            >
              {savingSection === section ? 'Saving…' : `Save ${SECTION_LABELS[section] || section}`}
            </button>
          </div>
        );
      })}

      {error && <div className="admin-error" style={{ marginTop: 16 }}>{error}</div>}
    </div>
  );
};
